import { Injectable } from '@angular/core';
import { BusinessHour } from '../models/business-hour.model';

export type BusinessHoursCategory = 'store' | 'delivery';

export type BusinessHoursMeta = {
  isOpen?: boolean;
  tenantTz?: string;
  tenantNow?: string;
};

export type DayHours = {
  day: string;
  isClosed?: boolean;
  open?: string | Date;
  close?: string | Date;
  intervals?: { open: string | Date; close: string | Date }[];
};

export type TodayHoursResult = {
  todayStoreHours?: BusinessHour;
  todayDeliveryHours?: BusinessHour;
};

export type DeliveryStatus =
  | 'before'
  | 'during'
  | 'after'
  | 'closedAllDay'
  | 'unknown';

@Injectable({
  providedIn: 'root',
})
export class BusinessHoursCalculatorService {
  readonly defaultTimezone = 'Europe/London';

  getNow(meta?: BusinessHoursMeta | null): Date {
    if (meta?.tenantNow) {
      return new Date(meta.tenantNow);
    }

    return new Date();
  }

  getTimezone(meta?: BusinessHoursMeta | null): string {
    return meta?.tenantTz?.replace('\\/', '/') || this.defaultTimezone;
  }

  isClosedVal(value: any): boolean {
    return value === 1 || value === '1' || value === true;
  }

  formatTime(time: string | null | undefined): string {
    if (!time) return '';

    const [hh, mm] = time.split(':').map((v) => parseInt(v, 10));

    if (Number.isNaN(hh) || Number.isNaN(mm)) {
      return '';
    }

    const ampm = hh >= 12 ? 'PM' : 'AM';
    const h12 = hh % 12 || 12;

    return `${h12}:${mm.toString().padStart(2, '0')} ${ampm}`;
  }

  formatTimeHHmm(time: string | null | undefined): string | null {
    if (!time) return null;

    const match = /^(\d{2}):(\d{2})(?::\d{2})?$/.exec(time.trim());

    if (!match) return null;

    return `${match[1]}:${match[2]}`;
  }

  getZoneTodayISO(timeZone: string, now = new Date()): string {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(now);

    const y = parts.find((p) => p.type === 'year')?.value;
    const m = parts.find((p) => p.type === 'month')?.value;
    const d = parts.find((p) => p.type === 'day')?.value;

    return `${y}-${m}-${d}`;
  }

  getWeekdayNameForISO(isoDate: string, timeZone: string): string {
    const dt = new Date(`${isoDate}T12:00:00`);

    return new Intl.DateTimeFormat('en-GB', {
      timeZone,
      weekday: 'long',
    }).format(dt);
  }

  addDaysISO(iso: string, days: number): string {
    const [y, m, d] = iso.split('-').map(Number);
    const dt = new Date(Date.UTC(y, m - 1, d));

    dt.setUTCDate(dt.getUTCDate() + days);

    return dt.toISOString().slice(0, 10);
  }

  timeToMinutes(time: string | null | undefined): number | null {
    if (!time) return null;

    const [hh, mm] = time.split(':').map(Number);

    if (Number.isNaN(hh) || Number.isNaN(mm)) {
      return null;
    }

    return hh * 60 + mm;
  }

  isOvernightHours(hour: BusinessHour | undefined | null): boolean {
    if (!hour) return false;

    const openMins = this.timeToMinutes(hour.openTime);
    const closeMins = this.timeToMinutes(hour.closeTime);

    if (openMins === null || closeMins === null) {
      return false;
    }

    return closeMins <= openMins;
  }

  buildDateTimeForISODate(isoDate: string, time: string): Date {
    const [y, m, d] = isoDate.split('-').map(Number);
    const [hh, mm] = time.split(':').map(Number);

    const dt = new Date();
    dt.setFullYear(y, m - 1, d);
    dt.setHours(hh, mm, 0, 0);

    return dt;
  }

  getHourWindow(hour: BusinessHour): { open: Date; close: Date } | null {
    if (
      this.isClosedVal(hour.isClosed) ||
      !hour.generatedDate ||
      !hour.openTime ||
      !hour.closeTime
    ) {
      return null;
    }

    const open = this.buildDateTimeForISODate(
      hour.generatedDate,
      hour.openTime,
    );

    const close = this.buildDateTimeForISODate(
      hour.generatedDate,
      hour.closeTime,
    );

    if (close <= open) {
      close.setDate(close.getDate() + 1);
    }

    return { open, close };
  }

  isNowWithinBusinessHour(hour: BusinessHour, now = new Date()): boolean {
    const window = this.getHourWindow(hour);

    if (window && now >= window.open && now < window.close) {
      return true;
    }

    // Handles overnight tail edge case:
    // row generatedDate = 2026-04-27, open 15:00, close 02:00
    // tenantNow = 2026-04-27 01:24
    // Treat as possible previous-day tail:
    // 2026-04-26 15:00 -> 2026-04-27 02:00
    if (!this.isOvernightHours(hour)) {
      return false;
    }

    if (
      this.isClosedVal(hour.isClosed) ||
      !hour.generatedDate ||
      !hour.openTime ||
      !hour.closeTime
    ) {
      return false;
    }

    const tailOpenDate = this.addDaysISO(hour.generatedDate, -1);

    const tailOpen = this.buildDateTimeForISODate(tailOpenDate, hour.openTime);

    const tailClose = this.buildDateTimeForISODate(
      hour.generatedDate,
      hour.closeTime,
    );

    return now >= tailOpen && now < tailClose;
  }

  getRelevantHoursForNow(
    all: BusinessHour[],
    category: BusinessHoursCategory,
    timeZone = this.defaultTimezone,
    now = new Date(),
  ): BusinessHour[] {
    const todayISO = this.getZoneTodayISO(timeZone, now);
    const yesterdayISO = this.addDaysISO(todayISO, -1);

    return (all || []).filter(
      (h) =>
        h.hoursCategory === category &&
        (h.generatedDate === todayISO || h.generatedDate === yesterdayISO),
    );
  }

  getActiveBusinessHourNow(
    all: BusinessHour[],
    category: BusinessHoursCategory,
    timeZone = this.defaultTimezone,
    now = new Date(),
  ): BusinessHour | undefined {
    return this.getRelevantHoursForNow(all, category, timeZone, now).find((h) =>
      this.isNowWithinBusinessHour(h, now),
    );
  }

  getTodayBusinessHour(
    all: BusinessHour[],
    category: BusinessHoursCategory,
    timeZone = this.defaultTimezone,
    now = new Date(),
  ): BusinessHour | undefined {
    const todayISO = this.getZoneTodayISO(timeZone, now);

    return (all || []).find(
      (h) => h.generatedDate === todayISO && h.hoursCategory === category,
    );
  }

  getTodayHours(
    all: BusinessHour[],
    timeZone = this.defaultTimezone,
    now = new Date(),
  ): TodayHoursResult {
    const todayDate = this.getZoneTodayISO(timeZone, now);
    const openingDay = this.getWeekdayNameForISO(todayDate, timeZone);

    const activeStoreHour = this.getActiveBusinessHourNow(
      all,
      'store',
      timeZone,
      now,
    );

    const activeDeliveryHour = this.getActiveBusinessHourNow(
      all,
      'delivery',
      timeZone,
      now,
    );

    const todayStoreHour = this.getTodayBusinessHour(
      all,
      'store',
      timeZone,
      now,
    );

    const todayDeliveryHour = this.getTodayBusinessHour(
      all,
      'delivery',
      timeZone,
      now,
    );

    const todayStoreHours =
      activeStoreHour ||
      todayStoreHour ||
      ({
        generatedDate: todayDate,
        tenantId: all?.[0]?.tenantId,
        hoursCategory: 'store',
        openingDay,
        openTime: null as any,
        closeTime: null as any,
        isClosed: '1' as any,
      } as BusinessHour);

    const todayDeliveryHours =
      activeDeliveryHour ||
      todayDeliveryHour ||
      ({
        generatedDate: todayDate,
        tenantId: all?.[0]?.tenantId,
        hoursCategory: 'delivery',
        openingDay,
        openTime: null as any,
        closeTime: null as any,
        isClosed: '1' as any,
      } as BusinessHour);

    return {
      todayStoreHours,
      todayDeliveryHours,
    };
  }

  isOpenNow(
    all: BusinessHour[],
    category: BusinessHoursCategory,
    timeZone = this.defaultTimezone,
    now = new Date(),
  ): boolean {
    return !!this.getActiveBusinessHourNow(all, category, timeZone, now);
  }

  getClosedFlag(
    all: BusinessHour[],
    category: BusinessHoursCategory = 'store',
    timeZone = this.defaultTimezone,
    now = new Date(),
  ): boolean {
    const activeHour = this.getActiveBusinessHourNow(
      all,
      category,
      timeZone,
      now,
    );

    if (activeHour) {
      return false;
    }

    const todayHour = this.getTodayBusinessHour(all, category, timeZone, now);

    if (!todayHour || this.isClosedVal(todayHour.isClosed)) {
      return true;
    }

    return true;
  }

  getNextOpening(
    all: BusinessHour[],
    category: BusinessHoursCategory = 'store',
    now = new Date(),
  ): BusinessHour | undefined {
    const future = (all || [])
      .filter((h) => {
        if (h.hoursCategory !== category) return false;
        if (this.isClosedVal(h.isClosed)) return false;

        const window = this.getHourWindow(h);

        if (!window) return false;

        return window.open > now;
      })
      .sort((a, b) => {
        const aWindow = this.getHourWindow(a);
        const bWindow = this.getHourWindow(b);

        if (!aWindow || !bWindow) return 0;

        return aWindow.open.getTime() - bWindow.open.getTime();
      });

    return future[0];
  }

  getDeliveryStatus(
    all: BusinessHour[],
    timeZone = this.defaultTimezone,
    now = new Date(),
  ): DeliveryStatus {
    const activeDeliveryHour = this.getActiveBusinessHourNow(
      all,
      'delivery',
      timeZone,
      now,
    );

    if (activeDeliveryHour) {
      return 'during';
    }

    const todayDeliveryHour = this.getTodayBusinessHour(
      all,
      'delivery',
      timeZone,
      now,
    );

    if (!todayDeliveryHour) {
      return 'closedAllDay';
    }

    if (this.isClosedVal(todayDeliveryHour.isClosed)) {
      return 'closedAllDay';
    }

    const window = this.getHourWindow(todayDeliveryHour);

    if (!window) {
      return 'unknown';
    }

    if (now < window.open) {
      return 'before';
    }

    return 'after';
  }

  toDayHoursFromBusinessHours(
    all: BusinessHour[],
    category: BusinessHoursCategory,
    timeZone = this.defaultTimezone,
    now = new Date(),
  ): DayHours[] {
    const toDayAbbrev: Record<string, string> = {
      Monday: 'Mon',
      Tuesday: 'Tue',
      Wednesday: 'Wed',
      Thursday: 'Thu',
      Friday: 'Fri',
      Saturday: 'Sat',
      Sunday: 'Sun',
    };

    const dayOrder = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

    const startISO = this.getZoneTodayISO(timeZone, now);
    const endISO = this.addDaysISO(startISO, 7);

    const norm = (t: string | null | undefined): string | undefined =>
      t == null || t === '' ? undefined : t;

    const windowRows = (all || []).filter(
      (h) =>
        h.hoursCategory === category &&
        h.generatedDate >= startISO &&
        h.generatedDate < endISO,
    );

    const byDay = new Map<string, BusinessHour[]>();

    windowRows.forEach((h) => {
      const label = toDayAbbrev[h.openingDay] || h.openingDay || '';
      const key = dayOrder.includes(label) ? label : h.openingDay;

      byDay.set(key, [...(byDay.get(key) || []), h]);
    });

    return dayOrder.map((day) => {
      const rows = byDay.get(day) || [];

      if (!rows.length) {
        return { day, isClosed: true };
      }

      const openRows = rows.filter((r) => !this.isClosedVal(r.isClosed));

      if (!openRows.length) {
        return { day, isClosed: true };
      }

      const intervals = openRows
        .map((r) => ({
          open: norm(r.openTime),
          close: norm(r.closeTime),
        }))
        .filter(
          (i): i is { open: string; close: string } =>
            typeof i.open === 'string' && typeof i.close === 'string',
        )
        .sort((a, b) => a.open.localeCompare(b.open));

      if (!intervals.length) {
        return { day, isClosed: true };
      }

      if (intervals.length === 1) {
        return {
          day,
          isClosed: false,
          open: intervals[0].open,
          close: intervals[0].close,
        };
      }

      return {
        day,
        isClosed: false,
        intervals,
      };
    });
  }
}
